import React, { useState } from "react";
import PropTypes from "prop-types";
import queryString from "query-string";
import {
  FormGroup,
  FormControl,
  FormLabel,
  Form,
  Button,
  Alert,
} from "react-bootstrap";
import FontAwesome from "react-fontawesome";
import azure from "resources/images/azure.svg";
import lock from "resources/images/lock.svg";
import okta from "resources/images/okta.svg";
import onelogin from "resources/images/onelogin.svg";

const PasswordInput = (props) => {
  const {
    emailAddress,
    password,
    identityProviderMode,
    allowPassword,
    identityProviderName,
    loginStep,
    handleIdPLogin,
    handlePasswordInput,
    returnToEmail,
    errorCode,
    errorMessage,
    liOnboarding,
    // mfaEnabled
  } = props;

  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const idPLogo = (() => {
    switch (identityProviderName.toLowerCase()) {
      case "okta":
        return okta;
      case "azuread":
        return azure;
      case "onelogin":
        return onelogin;
      // case 'auth0':
      default:
        return lock;
    }
  })();

  /* ----------- Setting UI ------------- */

  // Show Password Input for SSO-enabled accounts if:
  // Soft enabled, or admin log in
  // AND is not Company Specific Company SSO URL
  const allowPasswordLogin =
    (identityProviderMode === 1 ||
      (identityProviderMode !== 0 && allowPassword)) &&
    !(
      queryString.parse(window.location.search) &&
      queryString.parse(window.location.search).companyAccountId
    );

  let displayPasswordInputComponent = null;

  /*
  // if the account has MFA enabled
  // the password login flow should go through MFA
  // else allow users to manually input password for log in if conditions are met
  if (allowPasswordLogin && mfaEnabled === 2) {
    displayPasswordInputComponent = (
      <div className="text-center">
        <Button
          variant="primary"
          size="lg"
          id="logInWithMFA"
          style={{ width: '100%' }}
          onClick={() => handleIdPLogin('mfa')}
        >
          Log in with Password
        </Button>
      </div>
    );
  } else */

  // when identityProviderMode is null AND allowPassword is null, this is the default state. we should display passwordInput
  if (
    (allowPasswordLogin ||
      identityProviderMode === 0 ||
      (identityProviderMode === null && allowPassword === null)) &&
    emailAddress.length > 0
  ) {
    displayPasswordInputComponent = (
      <>
        <FormGroup>
          <FormLabel
            className="font-weight-bold"
            style={{
              fontSize: "14px",
              marginBottom: "2px",
            }}
          >
            Password
          </FormLabel>
          <div style={{ position: "relative" }}>
            <FormControl
              type={showPasswordConfirm ? "text" : "password"}
              placeholder="Enter your password here"
              id="loginPasswordInput"
              value={password}
              onChange={handlePasswordInput}
              data-test="password"
              required
            />
            <FontAwesome
              name={showPasswordConfirm ? "eye-slash" : "eye"}
              onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
              color="#A9AAAC"
              style={{
                color: "#A9AAAC",
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                height: "16px",
                width: "16px",
              }}
              size="lg"
            />
          </div>
          <Form.Control.Feedback type="invalid">
            Please enter a Password.
          </Form.Control.Feedback>
        </FormGroup>
        <div className="text-center mt-4">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            id="submitPassword"
            style={{ width: "100%", fontSize: "14px" }}
            className="font-weight-bold"
            disabled={password.length < 1}
          >
            Log in
          </Button>
        </div>
      </>
    );
  }

  const ssoEnabledWithAuth0 =
    identityProviderMode > 0 &&
    identityProviderName &&
    identityProviderName.toLowerCase() === "auth0";

  let ssoSignOnButtonText = "Your company uses Single Sign On (SSO)";
  if (identityProviderName && identityProviderName.toLowerCase() !== "auth0") {
    ssoSignOnButtonText += ` via ${identityProviderName}`;
  }

  return (
    <div
      style={{
        display: loginStep === 2 ? "" : "none",
      }}
    >
      <h5
        className="mb-3"
        style={{
          color: "#000",
          fontSize: "28px",
        }}
      >
        {liOnboarding ? "Welcome LinkedIn Talent Admin!" : "Welcome back"}
      </h5>
      {!(
        queryString.parse(window.location.search) &&
        queryString.parse(window.location.search).companyAccountId
      ) && (
        <div
          className="my-4"
          style={{
            border: "1px solid #A9AAAC",
            borderRadius: "4px",
            borderLeft: "6px solid #425CC7",
            padding: "12px 16px",
          }}
        >
          <p
            className="mb-1"
            style={{ textDecoration: "underline", fontSize: "16px" }}
          >
            {emailAddress}
          </p>
          <p
            className="mb-0"
            style={{
              fontSize: "14px",
              textDecoration: "underline",
              color: "#425CC7",
            }}
          >
            <button
              type="button"
              className="btn-as-link-no-state font-weight-bold"
              onClick={returnToEmail}
              id="loginAsSomeoneElseBtn"
              style={{ top: "0px" }}
            >
              Log in as someone else
            </button>
          </p>
        </div>
      )}
      {identityProviderMode > 0 && (
        <div className="text-center">
          <Button
            variant="primary"
            size="lg"
            id="logInWithIdP"
            style={{ width: "100%" }}
            onClick={() => handleIdPLogin(/* 'sso' */)}
          >
            <img
              src={idPLogo}
              alt="identityProviderLogo"
              height={20}
              width={20}
              style={{ marginRight: "6px" }}
            />
            Log in with {ssoEnabledWithAuth0 ? "SSO" : identityProviderName}
          </Button>
          <span>
            <FontAwesome
              name="key"
              style={{
                height: "16px",
                width: "16px",
              }}
            />
            {ssoSignOnButtonText}
          </span>
        </div>
      )}
      {allowPasswordLogin && <div className="linesWithMiddleText my-4">OR</div>}
      {errorCode && (
        <Alert className="error-messages" variant="danger">
          {errorMessage}
        </Alert>
      )}
      {displayPasswordInputComponent}
    </div>
  );
};

PasswordInput.propTypes = {
  emailAddress: PropTypes.string,
  password: PropTypes.string,
  identityProviderMode: PropTypes.number,
  allowPassword: PropTypes.bool,
  identityProviderName: PropTypes.string,
  loginStep: PropTypes.number,
  handleIdPLogin: PropTypes.func,
  handlePasswordInput: PropTypes.func,
  returnToEmail: PropTypes.func,
  errorCode: PropTypes.number,
  errorMessage: PropTypes.object,
  liOnboarding: PropTypes.bool,
  // mfaEnabled: PropTypes.number
};

export default PasswordInput;
